import { FindCustomersToOfflineOutput } from '../../../../../../../../../services/modules/customers/customers.types';
import { getSellerSalesRepresentative } from '../../../../../../../../../services/modules/salesRepresentatives/salesRepresentatives.repository';
import { getAllowedCustomerStatusToCreateOrder } from '../../../../../../../../orders/orderCreation.rules';
import { OfflineDatabaseCustomer } from '../../../../../../../database/offlineDatabase/types/offlineDatabaseCustomer.types';
import { offlineCustomersRepository } from '../../../../../../../repositories/offlineCustomers.repository';
import { QueryKeysAndFnOptions } from '../../../useQueryKeysAndFnToCache.types';
import { generateQueryKeysAndFnToPaginatedEndpoint } from '../../generateQueryKeysAndFnToPaginatedEndpoint';
import { QueryKeysAndFnGenerator, QueryKeysAndFnGeneratorParams } from './types';
import { mapSalesRepresentativeToOfflineData } from './utils/mapOfflineSalesRepresentative.util';

export class CustomerQueryKeysAndFnGenerator implements QueryKeysAndFnGenerator {
  constructor(
    private readonly params: Pick<QueryKeysAndFnGeneratorParams, 'priority' | 'generalSettings'>,
  ) {}

  async generateQueryKeysAndFn(): Promise<QueryKeysAndFnOptions> {
    const allowedStatusToCreate = getAllowedCustomerStatusToCreateOrder({
      allowBlockedCustomerToCreateOrder:
        this.params.generalSettings?.allowBlockedCustomerToCreateOrder,
    });

    const currentUserSalesRepResponse = await getSellerSalesRepresentative();
    if (!currentUserSalesRepResponse.ok) {
      throw currentUserSalesRepResponse.data;
    }

    const currentUserSalesRep = currentUserSalesRepResponse.data;

    return generateQueryKeysAndFnToPaginatedEndpoint<FindCustomersToOfflineOutput>({
      url: '/customers/offline/v1',
      priority: this.params.priority,
      params: { status: allowedStatusToCreate },
      fnToSaveData: async (data) => {
        await offlineCustomersRepository.saveAll(
          data.map((customer) => this.toOfflineData(customer, currentUserSalesRep?.id)),
        );
      },
    });
  }

  private toOfflineData(
    data: FindCustomersToOfflineOutput,
    userSalesRepresentativeId?: string,
  ): OfflineDatabaseCustomer {
    return {
      id: data.id,
      code: data.code,
      status: data.status,
      cpfcnpj: data.cpfcnpj,
      companyName: data.companyName,
      priceTableId: data.priceTableId,
      commercialName: data.commercialName,
      classificationIds: data.classificationIds,
      salesRepresentatives: this.mapSalesRepresentatives(
        data.salesRepresentatives,
        userSalesRepresentativeId,
      ),
    };
  }

  private mapSalesRepresentatives(
    salesReps: FindCustomersToOfflineOutput['salesRepresentatives'],
    userSalesRepresentativeId?: string,
  ) {
    return (
      salesReps?.map((salesRep) => {
        const isCurrentUserSalesRep = salesRep.id === userSalesRepresentativeId;
        return {
          principal: salesRep.principal,
          ...mapSalesRepresentativeToOfflineData({ ...salesRep, isMine: isCurrentUserSalesRep }),
        };
      }) ?? []
    );
  }
}
